import { forwardRef, useId, useState, type ChangeEvent } from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
} from '../../provider/context';
import type { BaseRadioGroupProps } from './types';
import { resolveBaseAppearance } from './theme';
import { cn, hasRenderableNode } from './utils';

function toDomId(value: string) {
  return value.replace(/[^a-zA-Z0-9_-]/g, '-');
}

const optionSizeClassNames = {
  sm: 'px-2 py-1.5 text-xs',
  md: 'px-3 py-2 text-sm',
  lg: 'px-4 py-3 text-base',
} as const;

const markerSizeClassNames = {
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
} as const;

const indicatorSizeClassNames = {
  sm: 'h-1.5 w-1.5',
  md: 'h-2 w-2',
  lg: 'h-2.5 w-2.5',
} as const;

export const BaseRadioGroup = forwardRef<
  HTMLDivElement,
  BaseRadioGroupProps
>(function BaseRadioGroup(
  {
    options = [],
    value,
    defaultValue,
    name,
    form,
    orientation = 'vertical',
    required = false,
    invalid = false,
    disabled = false,
    controlSize,
    appearance,
    className,
    classNames,
    style,
    children,
    onChange,
    onValueChange,
    id,
    'aria-invalid': nativeAriaInvalid,
    ...groupProps
  },
  ref,
) {
  for (const [stateName, stateValue] of [
    ['required', required],
    ['invalid', invalid],
    ['disabled', disabled],
  ] as const) {
    if (typeof stateValue !== 'boolean') {
      throw new TypeError(`BaseRadioGroup ${stateName} must be a boolean.`);
    }
  }
  if (orientation !== 'horizontal' && orientation !== 'vertical') {
    throw new TypeError(
      'BaseRadioGroup orientation must be "horizontal" or "vertical".',
    );
  }
  if (
    controlSize !== undefined &&
    controlSize !== 'sm' &&
    controlSize !== 'md' &&
    controlSize !== 'lg'
  ) {
    throw new TypeError(
      'BaseRadioGroup controlSize must be "sm", "md", or "lg".',
    );
  }
  if (!Array.isArray(options)) {
    throw new TypeError('BaseRadioGroup options must be an array.');
  }
  const optionValues = new Set<string>();
  options.forEach((option, index) => {
    if (!option || typeof option !== 'object') {
      throw new TypeError(
        `BaseRadioGroup option at index ${index} must be an object.`,
      );
    }
    if (typeof option.value !== 'string') {
      throw new TypeError(
        `BaseRadioGroup option value at index ${index} must be a string.`,
      );
    }
    if (optionValues.has(option.value)) {
      const duplicateValue = JSON.stringify(option.value);
      throw new TypeError(
        'BaseRadioGroup options must use unique values; ' +
          `duplicate value ${duplicateValue} at index ${index}.`,
      );
    }
    if (
      option.disabled !== undefined &&
      typeof option.disabled !== 'boolean'
    ) {
      throw new TypeError(
        `BaseRadioGroup option disabled at index ${index} must be a boolean.`,
      );
    }
    optionValues.add(option.value);
  });

  const resolvedAppearance = useMonkeysBaseAppearance(appearance);
  const componentAttributes = useMonkeysComponentAttributes();
  const theme = resolveBaseAppearance(resolvedAppearance);
  const generatedId = useId();
  const rootId = id ?? generatedId;
  const resolvedName = name ?? rootId;
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
  const isControlled = value !== undefined;
  const selectedValue = isControlled ? value : uncontrolledValue;

  const handleSelect = (
    event: ChangeEvent<HTMLInputElement>,
    optionDisabled?: boolean,
  ) => {
    if (disabled || optionDisabled) return;
    const nextValue = event.currentTarget.value;
    if (!isControlled) {
      setUncontrolledValue(nextValue);
    }
    onChange?.(event);
    onValueChange?.(nextValue, event);
  };

  return (
    <div
      {...componentAttributes}
      {...groupProps}
      ref={ref}
      id={id}
      role="radiogroup"
      aria-orientation={orientation}
      aria-required={required || undefined}
      aria-invalid={nativeAriaInvalid ?? (invalid || undefined)}
      aria-disabled={disabled || undefined}
      data-monkeys-component="radio-group"
      data-slot="root"
      data-state={disabled ? 'disabled' : 'enabled'}
      data-orientation={orientation}
      data-size={controlSize}
      data-required={required || undefined}
      data-invalid={invalid || undefined}
      data-disabled={disabled || undefined}
      className={cn(
        theme.slots.radioGroupRoot,
        orientation === 'horizontal' && 'flex flex-wrap items-center',
        classNames?.root,
        className,
      )}
      style={{
        ...theme.styles.radioGroup,
        ...style,
      }}
    >
      {options.map((option, index) => {
        const optionDisabled = disabled || option.disabled;
        const checked = option.value === selectedValue;
        const state = checked ? 'checked' : 'unchecked';
        const optionId = `${rootId}-${index}-${toDomId(option.value)}`;

        return (
          <label
            key={option.value}
            htmlFor={optionId}
            data-slot="option"
            data-state={state}
            data-size={controlSize}
            data-invalid={invalid || undefined}
            data-disabled={optionDisabled || undefined}
            className={cn(
              theme.slots.radioOption,
              optionDisabled && theme.slots.radioOptionDisabled,
              controlSize && optionSizeClassNames[controlSize],
              classNames?.option,
            )}
            style={theme.styles.radioOption}
          >
            <span
              data-slot="marker"
              data-state={state}
              data-size={controlSize}
              data-invalid={invalid || undefined}
              data-disabled={optionDisabled || undefined}
              className={cn(
                theme.slots.radioMarker,
                checked && theme.slots.radioMarkerChecked,
                controlSize && markerSizeClassNames[controlSize],
                classNames?.marker,
              )}
            >
              <input
                id={optionId}
                type="radio"
                name={resolvedName}
                form={form}
                value={option.value}
                checked={checked}
                required={required}
                disabled={optionDisabled}
                aria-invalid={nativeAriaInvalid ?? (invalid || undefined)}
                data-slot="input"
                data-state={state}
                data-size={controlSize}
                data-invalid={invalid || undefined}
                data-disabled={optionDisabled || undefined}
                className={cn(theme.slots.radioInput, classNames?.input)}
                onChange={(event) => handleSelect(event, option.disabled)}
              />
              {checked ? (
                <span
                  aria-hidden="true"
                  data-slot="indicator"
                  data-state={state}
                  data-size={controlSize}
                  className={cn(
                    theme.slots.radioIndicator,
                    controlSize && indicatorSizeClassNames[controlSize],
                    classNames?.indicator,
                  )}
                />
              ) : null}
            </span>
            {hasRenderableNode(option.icon) ? (
              <span
                data-slot="icon"
                className={cn('mt-0.5 shrink-0', classNames?.icon)}
              >
                {option.icon}
              </span>
            ) : null}
            <span
              data-slot="content"
              className={cn(theme.slots.radioContent, classNames?.content)}
            >
              <span
                data-slot="label"
                className={cn(theme.slots.radioLabel, classNames?.label)}
              >
                {option.label}
              </span>
              {hasRenderableNode(option.description) ? (
                <span
                  data-slot="description"
                  className={cn(
                    theme.slots.radioDescription,
                    classNames?.description,
                  )}
                >
                  {option.description}
                </span>
              ) : null}
            </span>
          </label>
        );
      })}
      {children}
    </div>
  );
});
